import { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useQuery } from '@tanstack/react-query';

// src/react/auth-provider.tsx
var STORAGE_KEY = "miror.appUrlOverrides";
function readStorage() {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const out = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "string" && v.length > 0) out[k] = v;
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}
function writeStorage(value) {
  if (typeof localStorage === "undefined") return;
  if (Object.keys(value).length === 0) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }
}
function normalizeUrl(url) {
  return url.trim().replace(/\/+$/, "");
}
function isValidAbsoluteUrl(url) {
  const trimmed = url.trim();
  if (!trimmed) return false;
  try {
    const u = new URL(trimmed);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
function useAppUrlOverrides() {
  const [overrides, setOverrides] = useState(() => readStorage());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setOverrides(readStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const setOverride = useCallback((appKey, baseUrl) => {
    if (!isValidAbsoluteUrl(baseUrl)) {
      throw new Error(`URL inv\xE1lida: "${baseUrl}". Use http:// ou https://`);
    }
    setOverrides((prev) => {
      const next = { ...prev, [appKey]: normalizeUrl(baseUrl) };
      writeStorage(next);
      return next;
    });
  }, []);
  const clearOverride = useCallback((appKey) => {
    setOverrides((prev) => {
      if (!(appKey in prev)) return prev;
      const next = { ...prev };
      delete next[appKey];
      writeStorage(next);
      return next;
    });
  }, []);
  const clearAll = useCallback(() => {
    setOverrides(() => {
      writeStorage({});
      return {};
    });
  }, []);
  const resolveUrl = useCallback(
    (appKey, fallback) => {
      return overrides[appKey] ?? fallback;
    },
    [overrides]
  );
  return {
    overrides,
    hasOverrides: Object.keys(overrides).length > 0,
    setOverride,
    clearOverride,
    clearAll,
    resolveUrl
  };
}
function clearAllAppUrlOverrides() {
  writeStorage({});
}
var AuthContext = createContext(null);
function AuthProvider({
  client,
  meUrl = "/v1/me",
  currentAppKey = null,
  activeCompanyId = null,
  children
}) {
  const [user, setUser] = useState(null);
  const [isHydrating, setHydrating] = useState(true);
  useEffect(() => {
    let cancelled = false;
    void client.axios.get(meUrl).then((res) => {
      if (!cancelled) setUser(res.data);
    }).catch(() => {
      if (!cancelled) setUser(null);
    }).finally(() => {
      if (!cancelled) setHydrating(false);
    });
    return () => {
      cancelled = true;
    };
  }, [client, meUrl]);
  const value = useMemo(
    () => ({
      client,
      user,
      isAuthenticated: user !== null,
      isHydrating,
      currentAppKey,
      activeCompanyId,
      login: async (email, password) => {
        await client.login(email, password);
        const res = await client.axios.get(meUrl);
        setUser(res.data);
      },
      logout: async () => {
        try {
          await client.logout();
        } finally {
          clearAllAppUrlOverrides();
          setUser(null);
        }
      }
    }),
    [client, user, isHydrating, meUrl, currentAppKey, activeCompanyId]
  );
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth* must be used inside <AuthProvider>");
  return ctx;
}
function useAuthClient() {
  return useAuthContext().client;
}
function useAuth() {
  const { client: _client, ...rest } = useAuthContext();
  return rest;
}
function useCurrentUser() {
  return useAuthContext().user;
}
function useMyApps(opts = {}) {
  const client = useAuthClient();
  const user = useCurrentUser();
  const endpoint = opts.endpoint ?? "/v1/me/apps";
  const enabled = opts.enabled ?? !!user;
  return useQuery({
    enabled,
    queryKey: ["miror", "me", "apps", user?.id ?? null],
    queryFn: async () => {
      const res = await client.axios.get(endpoint);
      return res.data;
    },
    staleTime: opts.staleTime ?? 6e4
  });
}
function useSsoLaunch(opts = {}) {
  const ctx = useContext(AuthContext);
  const overrides = useAppUrlOverrides();
  const [pendingKey, setPendingKey] = useState(null);
  const [error, setError] = useState(null);
  const endpoint = opts.endpoint ?? "/v1/sso/create-link";
  const timeoutMs = opts.timeoutMs ?? 3e3;
  const ttlSeconds = opts.ttlSeconds ?? 120;
  const activeCompanyId = opts.activeCompanyId !== void 0 ? opts.activeCompanyId : ctx?.activeCompanyId ?? null;
  const openNewTab = useCallback((url) => {
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);
  const createSsoLink = opts.createSsoLink;
  const authClient = ctx?.client;
  const launch = useCallback(
    async (item) => {
      const targetUrl = overrides.resolveUrl(item.key, item.baseUrl);
      setError(null);
      if (item.launchMethod === "url") {
        openNewTab(targetUrl);
        return;
      }
      if (!createSsoLink && !authClient) {
        const err = new Error(
          "useSsoLaunch: passe `createSsoLink` em opts OU embrulhe o app em <AuthProvider>."
        );
        setError(err);
        opts.onSsoFailure?.(item, err);
        openNewTab(targetUrl);
        return;
      }
      setPendingKey(item.key);
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        const body = {
          target_app: item.key,
          ttl_seconds: ttlSeconds,
          active_company_id: activeCompanyId
        };
        try {
          const data = createSsoLink ? await createSsoLink(body, controller.signal) : (await authClient.axios.post(endpoint, body, {
            signal: controller.signal
          })).data;
          clearTimeout(timer);
          const raw = data.redirect_url;
          const finalUrl = applyOriginOverride(raw, item.baseUrl, targetUrl);
          openNewTab(finalUrl);
        } finally {
          clearTimeout(timer);
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error("sso_failed");
        setError(err);
        opts.onSsoFailure?.(item, err);
        openNewTab(targetUrl);
      } finally {
        setPendingKey(null);
      }
    },
    [
      overrides,
      openNewTab,
      createSsoLink,
      authClient,
      endpoint,
      timeoutMs,
      ttlSeconds,
      activeCompanyId,
      opts
    ]
  );
  return { launch, pendingKey, error };
}
function applyOriginOverride(redirectUrl, catalogUrl, targetUrl) {
  if (targetUrl === catalogUrl) return redirectUrl;
  try {
    const u = new URL(redirectUrl);
    const t = new URL(targetUrl);
    u.protocol = t.protocol;
    u.host = t.host;
    return u.toString();
  } catch {
    return redirectUrl;
  }
}

export { AuthProvider, clearAllAppUrlOverrides, useAppUrlOverrides, useAuth, useAuthClient, useAuthContext, useCurrentUser, useMyApps, useSsoLaunch };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map